// Prepositions — the most-used ones with their core meanings, the articulated
// prepositions (al, del, nel…), and the a-vs-in choice for places. A
// frequency-ordered reference table of the most common ones sits at the bottom.
import { renderGuideSections } from "../ui/guideSections.js";

const MOST_USED = [
  ["di", "of / from", "il libro di Anna · sono di Roma"],
  ["a", "to / at / in", "vado a casa · alle due · a Milano"],
  ["da", "from / by / at someone's", "vengo da Roma · dal medico"],
  ["in", "in / to", "in Italia · in macchina · nel cassetto"],
  ["con", "with", "caffè con latte"],
  ["su", "on / about", "sul tavolo · un libro sull'arte"],
  ["per", "for / through / in order to", "per te · per imparare · parto per Roma"],
  ["tra / fra", "between / among / in (time)", "tra noi · fra un'ora"],
  ["senza", "without", "senza zucchero"],
  ["sotto", "under", "sotto il tavolo"],
  ["sopra", "above / on top of", "sopra il letto"],
  ["contro", "against", "contro il muro"],
  ["verso", "toward", "verso nord"],
  ["dopo", "after", "dopo cena"],
  ["durante", "during", "durante la lezione"],
];

export function prepositionsView(app) {
  const table = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Most-Used Prepositions</h3>
      <div class="overflow-x-auto"><table class="w-full text-sm">
        <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
          <th class="py-2 pr-3">Italian</th><th class="py-2 px-3">English</th><th class="py-2 px-3">Example</th>
        </tr></thead>
        <tbody>
          ${MOST_USED
            .map(
              ([es, en, ex]) => `<tr class="border-b border-sage-50 last:border-0">
            <td class="py-2 pr-3 font-medium text-sage-800">${es}</td>
            <td class="py-2 px-3 text-sage-700">${en}</td>
            <td class="py-2 px-3 text-sage-500 italic">${ex}</td>
          </tr>`
            )
            .join("")}
        </tbody>
      </table></div>
    </div>`;

  renderGuideSections(
    app,
    "Prepositions",
    "Prepositions link words together — showing place, time, direction, or relationship. Italian prepositions rarely map one-to-one onto English, so it pays to learn them in context rather than by single-word translation.",
    [
      {
        title: "1. Articulated prepositions — the big one",
        body: `
          <p class="text-sage-700 mb-2">When <b>di, a, da, in, su</b> meet a <b>definite article</b>, they fuse into one word. This is everywhere in Italian, so learn the grid:</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-2">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3"></th><th class="py-2 px-3">il</th><th class="py-2 px-3">lo</th><th class="py-2 px-3">la</th><th class="py-2 px-3">l'</th><th class="py-2 px-3">i</th><th class="py-2 px-3">gli</th><th class="py-2 px-3">le</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">a</td><td class="py-2 px-3">al</td><td class="py-2 px-3">allo</td><td class="py-2 px-3">alla</td><td class="py-2 px-3">all'</td><td class="py-2 px-3">ai</td><td class="py-2 px-3">agli</td><td class="py-2 px-3">alle</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">di</td><td class="py-2 px-3">del</td><td class="py-2 px-3">dello</td><td class="py-2 px-3">della</td><td class="py-2 px-3">dell'</td><td class="py-2 px-3">dei</td><td class="py-2 px-3">degli</td><td class="py-2 px-3">delle</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">da</td><td class="py-2 px-3">dal</td><td class="py-2 px-3">dallo</td><td class="py-2 px-3">dalla</td><td class="py-2 px-3">dall'</td><td class="py-2 px-3">dai</td><td class="py-2 px-3">dagli</td><td class="py-2 px-3">dalle</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">in</td><td class="py-2 px-3">nel</td><td class="py-2 px-3">nello</td><td class="py-2 px-3">nella</td><td class="py-2 px-3">nell'</td><td class="py-2 px-3">nei</td><td class="py-2 px-3">negli</td><td class="py-2 px-3">nelle</td></tr>
              <tr><td class="py-2 pr-3 font-medium">su</td><td class="py-2 px-3">sul</td><td class="py-2 px-3">sullo</td><td class="py-2 px-3">sulla</td><td class="py-2 px-3">sull'</td><td class="py-2 px-3">sui</td><td class="py-2 px-3">sugli</td><td class="py-2 px-3">sulle</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm"><i>Vado <b>al</b> cinema · la chiave <b>della</b> porta · esco <b>dal</b> lavoro · i libri sono <b>nello</b> zaino · <b>sui</b> tetti</i>. <i>con</i> usually stays separate (<i>con il</i>), though <i>col</i> survives.</p>
        `,
      },
      {
        title: "2. a or in? — places and motion",
        body: `
          <p class="text-sage-700 mb-2">Both can mean <i>to/in</i>, but Italian splits them by the kind of place.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>a</b> + cities and small places: <i>vado <b>a</b> Roma · sono <b>a</b> casa · <b>al</b> mare</i>.</li>
            <li><b>in</b> + countries, regions, and large areas: <i>vivo <b>in</b> Italia · <b>in</b> Toscana · <b>in</b> centro</i>.</li>
            <li><b>in</b> + means of transport and many rooms: <i><b>in</b> treno, <b>in</b> macchina, <b>in</b> ufficio, <b>in</b> cucina</i>.</li>
            <li><b>da</b> + a person = to/at their place: <i>vado <b>dal</b> medico · sono <b>da</b> Marco</i>.</li>
          </ul>
        `,
      },
      {
        title: "3. Verbs that come glued to a preposition",
        body: `
          <p class="text-sage-700 mb-2">Many verbs take a fixed preposition you simply memorise with the verb:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>pensare <b>a</b></i> (think about) · <i>cominciare <b>a</b></i> (start to) · <i>riuscire <b>a</b></i> (manage to)</li>
            <li><i>finire <b>di</b></i> (finish) · <i>cercare <b>di</b></i> (try to) · <i>avere bisogno <b>di</b></i> (need)</li>
            <li><i>credere <b>in</b></i> (believe in) · <i>parlare <b>con</b></i> (talk to)</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3">After a preposition, a verb stays in the <b>infinitive</b>: <i>prima di <b>uscire</b></i> (before leaving), <i>senza <b>pensare</b></i> (without thinking).</p>
        `,
      },
    ],
    "",
    table
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
